import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Phone, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { countryCodes } from '../../../utils/countryCodes';

const API = process.env.REACT_APP_BACKEND_URL;
// Mirrors the country-code dropdown on the signup form so the user gets
// the same picker they already know. The Map() dedupes shared dial codes
// (e.g. +1 US/Canada) to silence Radix Select duplicate-key warnings.
const UNIQUE_COUNTRY_CODES = Array.from(
  new Map(countryCodes.map((c) => [c.code, c])).values(),
);

const STEPS = { idle: 'idle', form: 'form', otp: 'otp', success: 'success' };

/**
 * Phone-change flow for the customer Profile page.
 *
 *   idle    → read-only phone row with edit pencil
 *   form    → country code + new number → "Send code"
 *   otp     → 4-digit code input → "Confirm" (or resend with cooldown)
 *   success → green tick + Done (auto-resets after 5 s)
 *
 * Spec 2026-05-26 (revised):
 *   - 4-digit OTP — matches signup + password-reset for one customer-side
 *     mental model. Brute-force budget is enforced by the 3-attempt
 *     lockout, not code length.
 *   - 3 wrong attempts then code invalidated server-side
 *   - 60-second cooldown between sends
 *   - "Same number entered" rejected at validation (server enforces too)
 */
export default function EditPhoneFlow() {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(STEPS.idle);
  const [busy, setBusy] = useState(false);

  // ----- form step state -----
  const [countryCode, setCountryCode] = useState('+673');
  const [localNumber, setLocalNumber] = useState('');
  const [cooldownSec, setCooldownSec] = useState(0);

  // ----- otp step state -----
  const [otp, setOtp] = useState('');
  const [pendingPhone, setPendingPhone] = useState('');

  // Initialise the country picker + local number from the current phone
  // each time we open the form (best-effort split on the longest matching
  // dial code).
  const seedFromCurrentPhone = () => {
    const current = user?.phone || '';
    const match = UNIQUE_COUNTRY_CODES
      .map((c) => c.code)
      .filter((code) => current.startsWith(code))
      .sort((a, b) => b.length - a.length)[0];
    if (match) {
      setCountryCode(match);
      setLocalNumber(current.slice(match.length));
    } else {
      setCountryCode('+673');
      setLocalNumber(current.replace(/^\+/, ''));
    }
    setOtp('');
    setCooldownSec(0);
  };

  // Tick down the resend cooldown timer each second while > 0.
  useEffect(() => {
    if (cooldownSec <= 0) return undefined;
    const t = setTimeout(() => setCooldownSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownSec]);

  const openForm = () => {
    seedFromCurrentPhone();
    setStep(STEPS.form);
  };
  const cancel = () => setStep(STEPS.idle);

  const sendOtp = async () => {
    const cleaned = localNumber.replace(/\D/g, '');
    if (cleaned.length < 6) {
      toast.error('Enter a valid phone number');
      return;
    }
    const candidate = `${countryCode}${cleaned}`;
    if (candidate === user?.phone) {
      toast.error('New phone must be different from your current number');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/me/phone-change/request-otp`,
        { new_phone: candidate },
        { withCredentials: true },
      );
      setPendingPhone(candidate);
      setStep(STEPS.otp);
      setCooldownSec(data?.cooldown_seconds || 60);
      toast.success('Code sent to your email');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'Could not send code';
      if (status === 429) {
        // Extract "Please wait N seconds" countdown into the cooldown timer.
        const m = /(\d+)\s*seconds?/i.exec(msg);
        if (m) setCooldownSec(parseInt(m[1], 10));
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    if (cooldownSec > 0) return;
    await sendOtp();
  };

  const confirmOtp = async () => {
    const code = otp.replace(/\D/g, '');
    if (code.length !== 4) {
      toast.error('Enter the 4-digit code');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/me/phone-change/confirm`,
        { new_phone: pendingPhone, otp: code },
        { withCredentials: true },
      );
      updateUser(data);
      setStep(STEPS.success);
      toast.success('Phone number updated');
      // Auto-close success view after 5s so the user gets back to the
      // read-only phone row without an extra tap.
      setTimeout(() => setStep(STEPS.idle), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not confirm code';
      toast.error(msg);
      // 429 / "Too many failed attempts" — the server invalidates the
      // record, so push the user back to the form to request a new code.
      if (err?.response?.status === 429) {
        setStep(STEPS.form);
        setCooldownSec(0);
      }
    } finally {
      setBusy(false);
    }
  };

  // ====================== render ======================

  if (step === STEPS.idle) {
    return (
      <div data-testid="profile-phone-row">
        <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Phone</p>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Phone className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <p className="font-mono text-sm truncate" data-testid="profile-phone-value">
              {user?.phone || 'Not set'}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={openForm}
            data-testid="profile-phone-edit-btn"
            aria-label="Edit phone"
            className="h-9 w-9 flex-shrink-0"
            style={{ background: '#D7FF6B', borderColor: '#9BBE3F' }}
          >
            <Pencil className="w-4 h-4 text-foreground" />
          </Button>
        </div>
      </div>
    );
  }

  if (step === STEPS.success) {
    return (
      <div
        className="flex flex-col items-center text-center py-6"
        data-testid="profile-phone-success"
      >
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-3">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <p className="font-bold text-lg">Phone updated</p>
        <p className="font-mono text-sm text-muted-foreground mt-1">{pendingPhone}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep(STEPS.idle)}
          className="mt-4"
          data-testid="profile-phone-success-done-btn"
        >
          Done
        </Button>
      </div>
    );
  }

  // form + otp share the same card; render header and bodies inline.
  return (
    <div className="space-y-4" data-testid="profile-phone-edit">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase font-bold text-muted-foreground">
          {step === STEPS.form ? 'New Phone Number' : 'Verify Code'}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={cancel}
          data-testid="profile-phone-cancel-btn"
          aria-label="Cancel"
          className="h-8 w-8"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {step === STEPS.form && (
        <>
          <div className="flex gap-2">
            <Select value={countryCode} onValueChange={setCountryCode}>
              <SelectTrigger
                className="h-12 w-[120px] rounded-xl"
                data-testid="profile-phone-country-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {UNIQUE_COUNTRY_CODES.map((c) => (
                  <SelectItem key={c.code + c.country} value={c.code}>
                    <span className="flex items-center gap-2">
                      <span>{c.flag}</span>
                      <span>{c.code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="Phone number"
              value={localNumber}
              onChange={(e) => setLocalNumber(e.target.value.replace(/\D/g, ''))}
              className="h-12 rounded-xl flex-1"
              data-testid="profile-phone-input"
              disabled={busy}
              maxLength={15}
            />
          </div>
          <Button
            type="button"
            onClick={sendOtp}
            disabled={busy}
            className="btn-primary w-full h-12"
            data-testid="profile-phone-send-otp-btn"
          >
            {busy ? 'Sending…' : 'Send verification code'}
          </Button>
          <p className="text-xs text-muted-foreground">
            We&apos;ll email a 4-digit code to <strong>{user?.email}</strong> to confirm the change.
          </p>
        </>
      )}

      {step === STEPS.otp && (
        <>
          <p className="text-sm">
            Enter the 4-digit code we emailed to <strong>{user?.email}</strong> to confirm changing your phone to:
          </p>
          <p className="font-mono text-sm bg-secondary/40 border rounded-lg px-3 py-2 text-center">
            {pendingPhone}
          </p>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="0000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="h-14 rounded-xl text-center font-mono text-2xl tracking-[0.5em]"
            data-testid="profile-phone-otp-input"
            disabled={busy}
            maxLength={4}
            autoFocus
          />
          <Button
            type="button"
            onClick={confirmOtp}
            disabled={busy || otp.length !== 4}
            className="btn-primary w-full h-12"
            data-testid="profile-phone-confirm-btn"
          >
            {busy ? 'Verifying…' : 'Confirm phone change'}
          </Button>
          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={resendOtp}
              disabled={cooldownSec > 0 || busy}
              className="font-semibold disabled:opacity-50"
              style={{ color: '#FF6B00' }}
              data-testid="profile-phone-resend-btn"
            >
              {cooldownSec > 0 ? `Resend in ${cooldownSec}s` : 'Resend code'}
            </button>
            <span className="text-muted-foreground">Code expires in 5 minutes</span>
          </div>
        </>
      )}
    </div>
  );
}
